import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreProvider';
import { ArrowRight } from 'lucide-react';

const CartScreen = () => {
  const { cart, updateCartQuantity, removeFromCart, getCartTotal } = useStore();
  const navigate = useNavigate();

  const totalItems = cart.reduce((total, item) => total + item.quantity, 0);

  if (cart.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-40">
        <h2 className="text-4xl font-serif mb-6 text-[#13110E]">Your bag is empty</h2>
        <Link 
          to="/store"
          className="border-b border-[#13110E] text-[10px] tracking-[0.2em] uppercase hover:opacity-60 transition-opacity pb-1"
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
      
      {/* Cart Items */}
      <div className="flex-1">
        <span className="text-[9px] tracking-[0.3em] text-[#13110E]/60 uppercase block mb-4">
          {totalItems === 1 ? 'ONE PIECE' : `${totalItems} PIECES`}
        </span>
        <h1 className="text-5xl md:text-7xl font-serif text-[#13110E] border-b border-[#13110E]/10 pb-8 mb-10">
          Your Bag
        </h1>
        
        <div className="space-y-10">
          {cart.map((item) => (
            <div key={`${item.product.id}-${item.variant_detail?.id || 'none'}`} className="flex gap-6 border-b border-[#13110E]/10 pb-10">
              {/* Image */}
              <div className="w-28 md:w-36 aspect-[4/5] bg-[#E5DFD5] flex-shrink-0">
                {item.product.images && item.product.images.length > 0 ? (
                  <img src={item.product.images[0].image} alt={item.product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#13110E]/30 font-serif italic text-xs">No Image</div>
                )}
              </div>
              
              {/* Details */}
              <div className="flex-1 flex flex-col justify-between">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[9px] tracking-[0.2em] text-[#13110E]/60 uppercase mb-2 block">
                      {item.product.category_detail?.name || 'AFRICAN WEAR'}
                    </span>
                    <Link to={`/store/product/${item.product.id}`} className="text-2xl font-serif text-[#13110E] hover:opacity-70 transition-opacity line-clamp-1 mb-1">
                      {item.product.name}
                    </Link>
                    {item.variant_detail && (
                      <span className="text-sm font-serif italic text-[#13110E]/80 block">
                        Size {item.variant_detail.size}
                      </span>
                    )}
                  </div>
                  
                  <div className="text-right flex flex-col items-end">
                    <span className="text-xl font-serif text-[#13110E] mb-3">
                      ₵{((item.variant_detail ? parseFloat(item.variant_detail.price) : parseFloat(item.product.price)) * item.quantity).toFixed(2)}
                    </span>
                    <button 
                      onClick={() => removeFromCart(item.product.id, item.variant_detail?.id)}
                      className="text-[9px] tracking-[0.2em] uppercase text-[#13110E]/60 border-b border-[#13110E]/30 pb-0.5 hover:text-[#13110E] hover:border-[#13110E] transition-all"
                    >
                      REMOVE
                    </button>
                  </div>
                </div>
                
                {/* Quantity */}
                <div className="flex items-center border border-[#13110E]/20 h-10 w-fit mt-4">
                  <button 
                    onClick={() => updateCartQuantity(item.product.id, item.quantity - 1, item.variant_detail?.id)}
                    className="w-10 flex items-center justify-center text-[#13110E] hover:bg-[#13110E]/5 transition-colors"
                  >
                    −
                  </button>
                  <span className="w-8 text-center text-xs font-serif">{item.quantity}</span>
                  <button 
                    onClick={() => updateCartQuantity(item.product.id, item.quantity + 1, item.variant_detail?.id)}
                    className="w-10 flex items-center justify-center text-[#13110E] hover:bg-[#13110E]/5 transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <Link 
            to="/store"
            className="text-[9px] tracking-[0.2em] uppercase text-[#13110E] border-b border-[#13110E] pb-1 hover:opacity-60 transition-opacity"
          >
            CONTINUE SHOPPING
          </Link>
        </div>
      </div>

      {/* Order Summary Block */}
      <div className="w-full lg:w-[400px] flex-shrink-0">
        <div className="bg-[#13110E] text-[#EBE6DD] p-10 sticky top-24">
          <h2 className="text-[9px] tracking-[0.3em] uppercase text-[#EBE6DD]/60 mb-10">
            ORDER SUMMARY
          </h2>
          
          <div className="space-y-6 mb-10 text-sm font-light">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span className="font-medium">₵{getCartTotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Shipping</span>
              <span>Calculated at checkout</span>
            </div>
          </div>
          
          <div className="border-t border-[#EBE6DD]/20 pt-10 mb-10 flex justify-between items-end">
            <span className="text-[9px] tracking-[0.3em] uppercase text-[#EBE6DD]/60 pb-2">Total</span>
            <span className="text-4xl font-serif text-[#EBE6DD]">
              ₵{getCartTotal().toFixed(2)}
            </span>
          </div>
          
          <button
            onClick={() => navigate('/store/checkout')}
            className="w-full bg-[#EBE6DD] text-[#13110E] py-4 flex items-center justify-center space-x-3 text-[10px] tracking-[0.3em] font-medium hover:bg-[#EBE6DD]/90 transition-colors group"
          >
            <span>CHECKOUT</span>
            <ArrowRight size={14} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

    </div>
  );
};

export default CartScreen;
